import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Send, Copy, Trash2, Mail, User as UserIcon, Loader2, Check, ExternalLink, RotateCw, ShieldAlert, Clock } from "lucide-react";

const STATUS_META = {
  active:  { label: "Active",   color: "#2563EB", bg: "#EFF6FF" },
  used:    { label: "Utilisée", color: "#059669", bg: "#ECFDF5" },
  revoked: { label: "Révoquée", color: "#DC2626", bg: "#FEF2F2" },
};

export default function InvitationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", parent_name: "", student_name: "", notes: "", days_valid: 14 });
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invitations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invitations", form);
      setLastCreated(data);
      setItems((arr) => [data, ...arr]);
      setForm({ email: "", parent_name: "", student_name: "", notes: "", days_valid: 14 });
      toast.success("Invitation créée");
    } catch (e) {
      toast.error(formatApiError(e, "Création impossible"));
    } finally {
      setCreating(false);
    }
  };

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const copy = async (id, token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopiedId(id);
      toast.success("Lien copié");
      setTimeout(() => setCopiedId(null), 1500);
    } catch (e) {
      toast.error("Copie impossible");
    }
  };

  const mailto = (inv) => {
    const url = inviteUrl(inv.token);
    const subject = encodeURIComponent("Votre accès personnel à MyLafayette Admissions — Lafayette International School");
    const body = encodeURIComponent(
`Bonjour ${inv.parent_name || ""},

Suite à notre rendez-vous, vous trouverez ci-dessous votre lien d'accès personnel à MyLafayette Admissions${inv.student_name ? ` pour ${inv.student_name}` : ""}.

⮕ ${url}

Ce lien est :
• PERSONNEL ET CONFIDENTIEL — ne le partagez pas
• À USAGE UNIQUE — une fois votre espace activé, le lien ne fonctionnera plus
• VALIDE ${Math.max(1, Math.round((new Date(inv.expires_at) - new Date()) / (1000*60*60*24)))} JOURS — passé ce délai, l'administration devra vous renvoyer un nouveau lien

Une fois votre espace activé, vous pourrez :
• Compléter le dossier d'inscription en ligne
• Téléverser les pièces justificatives (assurance scolaire, bulletins, pièces d'identité…)
• Joindre la preuve de virement des frais d'inscription
• Suivre la validation administrative en temps réel

Pour toute question, contactez-nous :
📞 +33 4 93 62 00 29
✉️ collegelyceelafayette@gmail.com
🌐 https://collegelyceeprivenice.com

À très bientôt,
L'administration de Lafayette International School
10 Avenue Georges Clemenceau · 06000 Nice`
    );
    return `mailto:${inv.email}?subject=${subject}&body=${body}`;
  };

  const regenerate = async (id) => {
    if (!window.confirm("Régénérer le lien ? L'ancien lien sera invalidé immédiatement.")) return;
    try {
      const { data } = await api.post(`/admin/invitations/${id}/regenerate`);
      setItems((arr) => arr.map((x) => (x.id === id ? data : x)));
      setLastCreated(data);
      toast.success("Lien régénéré · ancien lien invalidé");
    } catch (e) {
      toast.error(formatApiError(e, "Régénération impossible"));
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Révoquer cette invitation ?")) return;
    try {
      await api.delete(`/admin/invitations/${id}`);
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status: "revoked" } : x)));
      toast.success("Invitation révoquée");
    } catch (e) {
      toast.error(formatApiError(e, "Révocation impossible"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="card p-4 border-slate-800" style={{ background: "#0F172A", color: "#fff" }}>
        <div className="flex gap-3">
          <ShieldAlert style={{ color: "#FBBF24" }} className="mt-0.5 shrink-0" size={18}/>
          <div className="text-sm leading-relaxed" style={{ color: "#fff" }}>
            <div className="font-semibold" style={{ color: "#fff" }}>Sécurité du flux d'invitation</div>
            <ul className="text-xs mt-1.5 space-y-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>
              <li>• Chaque lien contient un jeton à 160 bits (cryptographiquement sûr)</li>
              <li>• Usage <strong style={{ color: "#fff" }}>unique</strong> : l'activation invalide automatiquement le lien</li>
              <li>• <strong style={{ color: "#fff" }}>Expiration</strong> automatique au bout du nombre de jours configuré</li>
              <li>• L'<strong style={{ color: "#fff" }}>email doit correspondre</strong> : un autre destinataire ne peut pas l'utiliser</li>
              <li>• Toute consultation et activation sont <strong style={{ color: "#fff" }}>tracées</strong> (IP + appareil) pour audit</li>
              <li>• Vous pouvez <strong style={{ color: "#fff" }}>révoquer</strong> ou <strong style={{ color: "#fff" }}>régénérer</strong> un lien à tout moment</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <div className="flex items-center gap-2">
          <Send size={16} className="text-amber-600"/>
          <h2 className="font-display text-lg font-semibold">Nouvelle invitation</h2>
        </div>
        <p className="text-xs text-slate-500 mt-1">Créez un lien d'accès personnel après le rendez-vous d'admission. Le parent ne pourra créer son compte qu'avec ce lien.</p>

        <form onSubmit={onCreate} className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="field-label">Email du parent</label>
            <input data-testid="inv-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-input" placeholder="famille@email.com"/>
          </div>
          <div>
            <label className="field-label">Nom du référent</label>
            <input data-testid="inv-parent-name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="field-input" placeholder="Famille Dupont"/>
          </div>
          <div>
            <label className="field-label">Nom de l'élève</label>
            <input data-testid="inv-student-name" value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} className="field-input" placeholder="Émile Dupont"/>
          </div>
          <div>
            <label className="field-label">Validité (jours)</label>
            <input data-testid="inv-days" type="number" min="1" max="60" value={form.days_valid} onChange={(e) => setForm({ ...form, days_valid: parseInt(e.target.value || 14, 10) })} className="field-input"/>
          </div>
          <div className="sm:col-span-2">
            <label className="field-label">Notes internes (RDV, niveau demandé…)</label>
            <textarea data-testid="inv-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="field-input min-h-[60px]" placeholder="RDV du 12/06 — entrée en 5e — fratrie dans l'école"/>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button data-testid="inv-submit" type="submit" disabled={creating} className="btn-primary">
              {creating ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
              {creating ? "Création…" : "Créer l'invitation"}
            </button>
          </div>
        </form>

        {lastCreated && (
          <div className="mt-4 card p-4 bg-emerald-50 border-emerald-200" data-testid="last-created">
            <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold"><Check size={14}/> Lien prêt à être envoyé</div>
            <div className="mt-2 flex gap-2 items-center flex-wrap">
              <code className="text-[11px] bg-white px-2 py-1.5 rounded border border-emerald-200 truncate max-w-md">{inviteUrl(lastCreated.token)}</code>
              <button onClick={() => copy(lastCreated.id, lastCreated.token)} className="btn-ghost text-xs"><Copy size={12}/> Copier</button>
              <a href={mailto(lastCreated)} className="btn-primary text-xs"><Mail size={12}/> Envoyer par email</a>
            </div>
            <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              <ShieldAlert size={14} className="mt-0.5 shrink-0"/>
              <div>
                <strong>Lien personnel et confidentiel.</strong> Il est à usage unique, à transmettre uniquement à <code>{lastCreated.email}</code>.
                Toute activation est tracée (IP + appareil) pour audit.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-semibold">Invitations envoyées</h3>
          <span className="text-xs text-slate-500">{items.length} au total</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="text-left p-3">Famille</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Audit</th>
              <th className="text-left p-3">Expire</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2"/> Chargement…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Aucune invitation pour le moment.</td></tr>
            )}
            {items.map((inv) => {
              const meta = STATUS_META[inv.status] || STATUS_META.active;
              return (
                <tr key={inv.id} className="border-t border-slate-100 align-top" data-testid={`inv-row-${inv.id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-2"><UserIcon size={12}/> {inv.parent_name || "—"}</div>
                    <div className="text-xs text-slate-400">{inv.student_name || ""}</div>
                  </td>
                  <td className="p-3 text-slate-700">{inv.email}</td>
                  <td className="p-3">
                    <span className="status-pill" style={{ color: meta.color, borderColor: meta.color }}>
                      <span className="dot" style={{ background: meta.color }}/>
                      {meta.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="text-[11px] text-slate-500 leading-relaxed">
                      <div className="flex items-center gap-1"><Clock size={10}/> {inv.lookup_count || 0} consultation{(inv.lookup_count || 0) > 1 ? "s" : ""}</div>
                      {inv.last_lookup_ip && (
                        <div className="text-slate-400">IP : <code className="text-[10px]">{inv.last_lookup_ip}</code></div>
                      )}
                      {inv.activation_ip && (
                        <div className="text-emerald-600">Activé · IP <code className="text-[10px]">{inv.activation_ip}</code></div>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-xs text-slate-500">{new Date(inv.expires_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-3 text-right">
                    {inv.status === "active" && (
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <button data-testid={`copy-${inv.id}`} onClick={() => copy(inv.id, inv.token)} className="btn-ghost text-xs">
                          {copiedId === inv.id ? <Check size={12}/> : <Copy size={12}/>} {copiedId === inv.id ? "Copié" : "Lien"}
                        </button>
                        <a href={mailto(inv)} className="btn-ghost text-xs" title="Envoyer par email"><Mail size={12}/></a>
                        <a href={`/invite/${inv.token}`} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs" title="Tester le lien"><ExternalLink size={12}/></a>
                        <button data-testid={`regen-${inv.id}`} onClick={() => regenerate(inv.id)} className="btn-ghost text-xs" title="Régénérer (invalide l'ancien)"><RotateCw size={12}/></button>
                        <button data-testid={`revoke-${inv.id}`} onClick={() => revoke(inv.id)} className="btn-ghost text-xs text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600" title="Révoquer"><Trash2 size={12}/></button>
                      </div>
                    )}
                    {inv.status !== "active" && (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
